import type { LineColumn } from './BillTemplate'
import type Customer from './Customer'
import type { PayMethod } from './Customer'
import type { DeliveryNote, Payload } from './DeliveryNote'
import type Place from './Place'
import type Price from './Price'
import type { PriceUnit, VatType } from './Price'

export enum BillStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED'
}

export enum BillLineType {
  STANDARD = 'STANDARD',
  CUSTOM = 'CUSTOM'
}

export enum BillType {
  STANDARD = 'STANDARD',
  SIMPLIFIED = 'SIMPLIFIED',
  CORRECTIVE = 'CORRECTIVE',
  SIMPLIFIED_CORRECTIVE = 'SIMPLIFIED_CORRECTIVE'
}

export interface StatusChangeEvent {
  id: string
  fromStatus?: BillStatus
  toStatus: BillStatus
  reason?: string
  notes?: string
  changeDate: string
  changedBy?: string
}

export interface BillDraftLine {
  id?: string
  customerId: string
  customerName: string
  deliveryNoteDate: string
  quantity: number
  description: string
  origin: Place
  destination: Place
  unit: PriceUnit
  payload: string
  price?: Price
  deliveryNoteIds: string[]
  deliveryNotePayloadIds: string[]
  deliveryNotes?: DeliveryNote[]
  isCustomLine?: boolean
  lineType?: BillLineType
  temporary?: boolean
  temporaryPrice?: {
    unit: PriceUnit
    unitPrice: number
    vatRate: number
    vatType: VatType
  }
  // Properties for custom lines
  unitPrice?: number
  vatRate?: number
  retentionPercentage?: number
  retentionAmount?: number
  totalAmount?: number
  totalExecutableAmount?: number
  originPlaceId?: string
  destinationPlaceId?: string
  vatType?: VatType
}

export interface BillCustomLine {
  quantity: number
  description: string
  unitPrice: number
  vatRate: number
  vatType: VatType
  unit: PriceUnit
  isCustomLine: true
  retentionPercentage?: number
  retentionAmount?: number
  totalAmount?: number
  totalExecutableAmount?: number
  id?: string
  deliveryNoteDate?: string
  deliveryNoteIds?: string[]
  deliveryNotePayloadIds?: string[]
  deliveryNotes?: DeliveryNote[]
}

export interface CustomLineDeliveryNoteContext {
  deliveryNoteDate?: string
  deliveryNoteIds?: string[]
  deliveryNotePayloadIds?: string[]
  deliveryNotes?: DeliveryNote[]
  unit?: PriceUnit
  origin?: Place
  destination?: Place
}

export function isPayloadSelectedForBillLine(
  payload: Payload,
  selectedPayloadIds?: string[]
): boolean {
  if (!selectedPayloadIds?.length) {
    return false
  }

  return (
    selectedPayloadIds.includes(payload.id) ||
    !!(payload.payloadId && selectedPayloadIds.includes(payload.payloadId))
  )
}

export function filterDeliveryNotesForBillLine(
  deliveryNotes: DeliveryNote[] | undefined,
  selectedPayloadIds?: string[]
): DeliveryNote[] | undefined {
  if (!deliveryNotes?.length || !selectedPayloadIds?.length) {
    return deliveryNotes
  }

  return deliveryNotes
    .map((deliveryNote) => ({
      ...deliveryNote,
      payloads: (deliveryNote.payloads ?? []).filter((payload) =>
        isPayloadSelectedForBillLine(payload, selectedPayloadIds)
      )
    }))
    .filter((deliveryNote) => deliveryNote.payloads.length > 0)
}

export function toCustomLineDeliveryNoteContext(
  line?: Partial<BillDraftLine | BillCustomLine>
): CustomLineDeliveryNoteContext | undefined {
  if (!line || !hasDeliveryNoteLinkage(line)) {
    return undefined
  }

  const filteredDeliveryNotes = filterDeliveryNotesForBillLine(
    line.deliveryNotes,
    line.deliveryNotePayloadIds
  )

  return {
    deliveryNoteDate: line.deliveryNoteDate,
    deliveryNoteIds: filteredDeliveryNotes?.map((deliveryNote) => deliveryNote.id) ?? line.deliveryNoteIds,
    deliveryNotePayloadIds: line.deliveryNotePayloadIds,
    deliveryNotes: filteredDeliveryNotes,
    unit: line.unit,
    origin: 'origin' in line ? line.origin : undefined,
    destination: 'destination' in line ? line.destination : undefined
  }
}

export function hasDeliveryNoteLinkage(line: Partial<BillDraftLine>): boolean {
  return !!(
    line.deliveryNoteIds?.length ||
    line.deliveryNotePayloadIds?.length ||
    line.deliveryNotes?.length
  )
}

/** Custom line added manually (not linked to transport documents). */
export function isManualCustomLine(line: BillDraftLine): boolean {
  return !!line.isCustomLine && !hasDeliveryNoteLinkage(line)
}

export function getCollapseLineKey(line: BillDraftLine): string {
  return `${line.description ?? ''}||${line.unitPrice ?? 0}||${line.vatRate ?? 0}||${line.unit ?? ''}`
}

export function findCustomLineIndex(
  lines: BillDraftLine[],
  line: BillCustomLine & { id?: string }
): number {
  if (line.id) {
    const byId = lines.findIndex((l) => l.isCustomLine && l.id === line.id)
    if (byId !== -1) {
      return byId
    }
  }
  return lines.findIndex((l) => l.isCustomLine && l === line)
}

/** Keeps delivery-note linkage when saving custom line dialog over existing draft line */
export function preserveCustomLineDeliveryContext(
  draftLine: BillDraftLine,
  source?: Partial<BillDraftLine>
): BillDraftLine {
  if (!source) {
    return draftLine
  }

  const hasPlace = (place?: Place) => !!(place?.id || place?.name)

  return {
    ...draftLine,
    id: source.id ?? draftLine.id,
    deliveryNoteDate: source.deliveryNoteDate ?? draftLine.deliveryNoteDate,
    deliveryNoteIds: source.deliveryNoteIds ?? draftLine.deliveryNoteIds,
    deliveryNotePayloadIds: source.deliveryNotePayloadIds ?? draftLine.deliveryNotePayloadIds,
    deliveryNotes: source.deliveryNotes ?? draftLine.deliveryNotes,
    origin: hasPlace(source.origin) ? source.origin! : draftLine.origin,
    destination: hasPlace(source.destination) ? source.destination! : draftLine.destination,
    payload: source.payload ?? draftLine.payload,
    lineType: source.lineType ?? draftLine.lineType,
    originPlaceId: source.originPlaceId ?? draftLine.originPlaceId,
    destinationPlaceId: source.destinationPlaceId ?? draftLine.destinationPlaceId
  }
}

export interface BillDraft {
  lines: BillDraftLine[]
}

export interface BillVatLine {
  baseAmount: number
  vat: number
  vatType: VatType
  vatAmount: number
  totalAmount: number
  retentionAmount?: number
}

export interface DeliveryNoteBill {
  id: string
  number: string
  origin: Place
  destination: Place
  payload: string
  payloadId: string
  unit: PriceUnit
  quantity: number
  notes: string
  date: string

}

export interface BillLineWithDeliveryNotes extends BillLine {
  deliveryNotes?: DeliveryNoteBill[]
}

export interface BillLine {
  id: string
  description: string
  quantity: number
  origin: Place
  destination: Place
  price?: Price
  unit: PriceUnit
  amount: number
  baseAmount: number
  vat: number
  vatAmount: number
  vatType: VatType
  retentionPercentage?: number
  retentionAmount?: number
  totalAmount: number
  totalExecutableAmount?: number
  lineType: BillLineType
  temporary?: boolean
  deliveryNotes?: DeliveryNoteBill[]
}

export interface BillTaxLine {
  vatType: VatType
  baseAmount: number
  vat: number
  vatAmount: number
  totalAmount: number
}

export interface Bill {
  id: string
  number: string
  autogenerateNumber?: boolean
  type: BillType
  reference?: string
  customer?: Customer
  date: string
  baseAmount: number
  totalAmount: number
  vatAmount: number
  retentionAmount?: number
  totalExecutableAmount?: number
  lines: BillLine[]
  status?: BillStatus
  dueDate?: string
  statusHistory: StatusChangeEvent[]
  approved?: boolean
  approvalDate?: string
  annulled?: boolean
  annulationDate?: string
  billTaxLines: BillTaxLine[]
  notes?: string
  billTemplateId?: string
  billTemplateLineColumns?: LineColumn[]
  payMethod?: PayMethod
  bankAccountNumber?: string
  paymentDescription?: string
  originalBillToRecoverId?: string
}

export interface BillResponse {
  bills: Bill[]
}

export interface DeliveryNotesByBillLineResponse {
  deliveryNotes: DeliveryNote[]
}

export interface BillLineRequest {
  priceId?: string
  deliveryNoteDate: string
  deliveryNotePayloadIds: string[]
  description: string
  retentionPercentage?: number
  temporary?: boolean
  quantity?: number
  vatType?: VatType
  vatRate?: number
  unit?: PriceUnit
}

export interface CustomBillLineRequest {
  description: string
  quantity: number
  unitPrice: number
  vatRate: number
  vatType: VatType
  unit: PriceUnit
  retentionPercentage?: number
  deliveryNotePayloadIds?: string[]
}

export interface BillRequest {
  customerId: string
  type: BillType
  number: string
  autogenerateNumber: boolean
  reference?: string
  date: string
  dueDate?: string
  lines: BillLineRequest[]
  customLines?: CustomBillLineRequest[]
  selectAll: boolean
  selectedIds: string[]
  unselectedIds: string[]
  notes?: string
  billTemplateId?: string
  billTemplateLineColumns?: LineColumn[]
  payMethod?: PayMethod
  bankAccountNumber?: string
  paymentDescription?: string
  originalBillToRecoverId?: string
}

export interface BillStatusUpdateRequest {
  newStatus: BillStatus
  reason?: string
  notes?: string
  timestamp: string
}

export interface BillLineUpdateRequest {
  id: string
  description: string
  priceId?: string
  retentionPercentage?: number
  deliveryNotePayloadIds: string[]
  temporary?: boolean
  quantity?: number
  vatType?: VatType
  vatRate?: number
  unit?: PriceUnit
}

export interface CustomBillLineUpdateRequest {
  id?: string
  description: string
  quantity: number
  unitPrice: number
  vatRate: number
  vatType: VatType
  unit: PriceUnit
  retentionPercentage?: number
  deliveryNotePayloadIds?: string[]
}

export interface BillUpdateRequest {
  number: string
  autogenerateNumber?: boolean
  reference?: string
  date: string
  lines: BillLineUpdateRequest[]
  customLines?: CustomBillLineUpdateRequest[]
  notes?: string
  billTemplateId?: string
  billTemplateLineColumns?: LineColumn[]
  paymentDescription?: string
  dueDate?: string
  payMethod?: PayMethod
  bankAccountNumber?: string
}
